import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/isMobile/isMobile";
import Avatar from "../../profile/Avatar";
import { User } from "../../types/user";

interface MembersProps {
  owner: User;
  admins: User[];
  members: User[];
}

const Members = ({ owner, admins, members }: MembersProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const theme = useTheme();

  // Create a combined list of all members with their roles
  const allMembers = [
    ...admins.map((admin) => ({ user: admin, role: "admin" as const })),
    ...members
      .filter((member) => !admins.some((admin) => admin.email === member.email))
      .map((member) => ({ user: member, role: "member" as const })),
  ];

  // Sort members: owner first, then admins, then regular members
  allMembers.sort((a, b) => {
    if (a.user.email === owner.email) return -1;
    if (b.user.email === owner.email) return 1;
    if (a.role === "admin" && b.role === "member") return -1;
    if (a.role === "member" && b.role === "admin") return 1;
    return a.user.displayName.localeCompare(b.user.displayName);
  });

  const getRoleEmojis = (memberEmail: string, role: string) => {
    let roles = "";
    if (memberEmail === owner.email) roles += "👑"; // Crown for owner
    if (role === "admin") roles += "⭐"; // Star for admin
    if (roles === "") return "👤"; // User for regular member
    return roles;
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Stack spacing={2}>
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            px: 1,
            color:
              theme.palette.mode === "dark"
                ? "#1C6B50"
                : theme.palette.text.primary,
          }}
        >
          League Members ({allMembers.length})
        </Typography>
        <Stack spacing={1} sx={{ px: 1 }}>
          {allMembers.map((member, index) => (
            <Card
              key={index}
              onClick={() => navigate(`/members/${member.user.email}`)}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "action.hover",
                  transform: "translateY(-1px)",
                  transition: "all 0.2s ease-in-out",
                },
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    email={member.user.email}
                    alt={member.user.displayName}
                    sx={{
                      bgcolor:
                        member.user.email === owner.email
                          ? "primary.main"
                          : member.role === "admin"
                          ? "secondary.main"
                          : "grey.300",
                      width: 40,
                      height: 40,
                      fontSize: "1.1em",
                    }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight:
                          member.user.email === owner.email ? "bold" : "normal",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {member.user.displayName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.8em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {member.user.email}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span style={{ fontSize: "1em" }}>
                        {getRoleEmojis(member.user.email, member.role)}
                      </span>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    );
  }

  // Desktop table view
  return (
    <Stack spacing={2}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color:
            theme.palette.mode === "dark"
              ? "#1C6B50"
              : theme.palette.text.primary,
        }}
      >
        League Members ({allMembers.length})
      </Typography>
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="league members table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1C6B50" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Display Name
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Email
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allMembers.map((member, index) => (
              <TableRow
                key={index}
                onClick={() => navigate(`/members/${member.user.email}`)}
                hover
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight:
                      member.user.email === owner.email ? "bold" : "normal",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      email={member.user.email}
                      alt={member.user.displayName}
                      sx={{
                        bgcolor:
                          member.user.email === owner.email
                            ? "primary.main"
                            : member.role === "admin"
                            ? "secondary.main"
                            : "grey.300",
                        width: 40,
                        height: 40,
                        fontSize: "1.1em",
                      }}
                    />
                    <span style={{ fontSize: "1.2em" }}>
                      {getRoleEmojis(member.user.email, member.role)}
                    </span>
                    <span>{member.user.displayName}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: "0.9em" }}>
                  {member.user.email}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default Members;
